import {
  Card,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  Col,
  IconButton,
  Row,
  Button,
  ArrowLeftIcon,
  Badge,
  TextDisplay,
  HeadingText,
} from '@paypalcorp/pp-react'
import React, { useState } from 'react'
import { Message, injectWorldReady } from '@paypalcorp/worldready-react'
import { useApplicationContext } from '../../../context/ApplicationContext'
import PaymentSolutions from './payment-solutions'
import SandboxPayments from './sandbox-payments'
import AppCredentials from './app-credentials'
import UpgradeAccount from './upgrade-account'
import LiveCredentialsFromClient from './live-credentials-from-client'
import CreateLiveApp from './create-live-app'
import Spinner from '../../../components/common/spinner'
import { recordDynamicEvent } from '../../../components/common/analytics/utils'
import IntegrationGuide from './integration-guide'
import DoneTesting from './done-testing'
import { saveBannerProgress } from '../../../utils/session-utils'
import { getUserType } from '../../../utils/user-utils'
import apiFactory from '../../../utils/api-factory'
import FullAuthNeeded from '../../applications/sections/full-auth-needed'

function OnboardingBanner({
  closeBanner,
  bannerPage,
  setBannerPage,
  productIntegration,
  setProductIntegration,
  setIsSavingUserPreferences,
}) {
  const [isMinimized, setIsMinimized] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [isCredsForClient, setIsCredsForClient] = useState(false)

  const {
    logger,
    csrf,
    user,
    setReloadPage,
    setCurrentEnvironment,
    enviroment,
    sandboxApps,
    liveApps,
    homePageDataLoading,
    setCurrentBannerStep,
    isWebLLSUser,
  } = useApplicationContext()

  const accountType = getUserType(user)

  const sbApps = sandboxApps
  let sbApp
  if (sbApps?.length > 0) {
    sbApp = sbApps?.find((e) => e.isSystemGenerated === true) || sbApps[0]
  }

  const apps = liveApps
  let liveApp
  if (apps?.length > 0) {
    liveApp = apps[0]
  }

  function clickHandler() {
    if (isMinimized) {
      setIsMinimized(false)
    } else {
      setIsMinimized(true)
    }
  }

  const addUserPreference = async () => {
    setIsSavingUserPreferences(true)
    const userBannerPreference = {
      preferenceName: 'ONBOARDING_BANNER',
      preferenceValue: 'CLOSED',
    }

    await apiFactory.addUserPreference(csrf, userBannerPreference)
    setIsSavingUserPreferences(false)
  }

  function closeHandler() {
    closeBanner(true)
    recordDynamicEvent(`onboarding_banner_close,click,${bannerPage}`)

    const sessionObject = {
      bannerPage,
      productIntegration,
      closeOnboardingBanner: true,
    }

    setCurrentBannerStep(sessionObject)
    saveBannerProgress(csrf, logger, sessionObject)
    addUserPreference()
  }

  function goBackHandler() {
    let targetBannerPage = bannerPage - 1
    if (bannerPage === 2) {
      targetBannerPage = 0
    }
    const sessionObject = {
      bannerPage: targetBannerPage,
      productIntegration,
    }
    setCurrentBannerStep(sessionObject)
    saveBannerProgress(csrf, logger, sessionObject)
    setBannerPage(targetBannerPage)
    if (bannerPage === 3 && enviroment === 'live') {
      setCurrentEnvironment('sandbox')
      setReloadPage('yes')
    }
    recordDynamicEvent(
      `onboarding_banner_go_back,click,goback,step${bannerPage}`,
    )
  }

  const bannerTitle = () => {
    let title = 'onboarding-banner.paymentSolutionTitle'
    if (bannerPage === 1) {
      title = `onboarding-banner.product.${productIntegration}.title`
    } else if (bannerPage === 2) {
      title = 'onboarding-banner.sandboxCredentialsTitle'
    } else if (bannerPage === 3) {
      title = 'onboarding-banner.sandboxPaymentsTitle'
    } else if (bannerPage === 4) {
      if (isCredsForClient === true) {
        title = 'onboarding-banner.liveCredentialsTitle'
      } else if (accountType === 'BUSINESS' && liveApp === undefined) {
        title = 'onboarding-banner.createLiveAppTitle'
      } else {
        title = 'onboarding-banner.livePaymentsTitle'
      }
    } else if (bannerPage === 5) {
      title = 'onboarding-banner.doneTestingTitle'
    }
    return title
  }

  let MinimizedBanner = () => {
    return (
      <Card>
        <Row>
          <Col xs={10}>
            <TextDisplay
              value={<Message id={bannerTitle()} />}
              className="banner-title-minimized banner-title"
            />
          </Col>
          <Col align="right">
            <IconButton
              size="sm"
              onClick={clickHandler}
              buttonType="tertiary"
              svgIconComponent={ChevronDownIcon}
            />
            <IconButton
              size="sm"
              onClick={closeHandler}
              buttonType="tertiary"
              svgIconComponent={CloseIcon}
              style={{
                marginLeft: '12px',
                marginRight: '12px',
              }}
            />
          </Col>
        </Row>
      </Card>
    )
  }

  //eslint-disable-next-line complexity
  const BannerContent = () => {
    return (
      <>
        {bannerPage === 0 && (
          <PaymentSolutions
            setBannerPage={setBannerPage}
            setProductIntegration={setProductIntegration}
            tabIndex={tabIndex}
            setTabIndex={setTabIndex}
            setCurrentBannerStep={setCurrentBannerStep}
          />
        )}

        {bannerPage === 1 && (
          <IntegrationGuide
            setBannerPage={setBannerPage}
            productIntegration={productIntegration}
          />
        )}

        {bannerPage === 2 && (
          <AppCredentials
            setBannerPage={setBannerPage}
            app={sbApp}
            env="sandbox"
          />
        )}

        {bannerPage === 3 && <SandboxPayments setBannerPage={setBannerPage} />}

        {bannerPage === 4 && (
          <>
            {isWebLLSUser && accountType === 'BUSINESS' && (
              <>
                <Row>
                  <Col xs={10} className="banner-title_row">
                    <HeadingText size="sm">
                      <Message id="onboarding-banner.livePaymentsTitle" />
                    </HeadingText>
                  </Col>
                </Row>
                <FullAuthNeeded />
              </>
            )}

            {!isWebLLSUser && isCredsForClient === true && (
              <LiveCredentialsFromClient
                setIsCredsForClient={setIsCredsForClient}
              />
            )}

            {!isWebLLSUser &&
              isCredsForClient === false &&
              accountType === 'BUSINESS' && (
                <>
                  {liveApp !== undefined && (
                    <AppCredentials
                      setBannerPage={setBannerPage}
                      app={liveApp}
                      env="live"
                    />
                  )}
                  {liveApp === undefined && (
                    <>
                      {homePageDataLoading === true && <Spinner size="md" />}
                      {!homePageDataLoading && (
                        <CreateLiveApp setBannerPage={setBannerPage} />
                      )}
                    </>
                  )}
                </>
              )}

            {!isWebLLSUser &&
              isCredsForClient === false &&
              accountType !== 'BUSINESS' && (
                <UpgradeAccount
                  setBannerPage={setBannerPage}
                  setIsCredsForClient={setIsCredsForClient}
                />
              )}
          </>
        )}
        {bannerPage === 5 && <DoneTesting />}
        <br />
      </>
    )
  }

  return (
    <>
      {isMinimized ? (
        <MinimizedBanner />
      ) : (
        <Card>
          <Row className="banner-header">
            {bannerPage > 0 && bannerPage < 5 ? (
              <>
                <Col xs={2}>
                  <IconButton
                    size="xs"
                    onClick={goBackHandler}
                    buttonType="tertiary"
                    svgIconComponent={ArrowLeftIcon}
                  />
                  <Button
                    tertiary
                    size="xs"
                    onClick={goBackHandler}
                    style={{ top: '-5px' }}
                  >
                    <Message id="onboarding-banner.goBack" />
                  </Button>
                </Col>

                <Col xs={6} />
                <Col xs={2} align="right">
                  <Badge className="banner-badge" type="neutral">
                    <Message
                      id="onboarding-banner.stepBadge"
                      number={bannerPage}
                      total={'4'}
                    />
                  </Badge>
                </Col>
              </>
            ) : (
              <Col xs={10}>
                <TextDisplay
                  className="banner-title"
                  value={<Message id={bannerTitle()} />}
                />
              </Col>
            )}
            <Col align="right">
              {bannerPage !== 5 && (
                <IconButton
                  size="sm"
                  onClick={clickHandler}
                  buttonType="tertiary"
                  svgIconComponent={ChevronUpIcon}
                />
              )}
              <IconButton
                size="sm"
                onClick={closeHandler}
                buttonType="tertiary"
                svgIconComponent={CloseIcon}
                style={{
                  marginLeft: '12px',
                  marginRight: '12px',
                }}
              />
            </Col>
          </Row>
          <BannerContent />
        </Card>
      )}
    </>
  )
}

export default injectWorldReady(OnboardingBanner)
